import { FC, useEffect, useRef, useState } from "react";
import { cloneDeep } from "lodash";
import { useFormContext } from "react-hook-form";
import { Box, Tab, Tabs } from "@material-ui/core";

import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { FormType } from "../../common";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";

import {
  CattleHousing,
  CattleHousingAndManure,
  TargetAnimal,
} from "../../../../models/Intervention/CattleIntervention";
import {
  CattleHousing as CattleHousingBaseline,
  CattleHousingAndManure as CattleHousingAndManureBaseline,
} from "../../../../models/Baseline/CattleBaseline";
import {
  BeefBreedingStageDataIntervention,
  BeefGrowingStageDataIntervention,
  BeefIntervention,
  BeefStageIntervention,
} from "../../../../models/Intervention/BeefIntervention";
import BeefHousingAndManureForm from "./BeefHousingAndManureForm";
import {
  BeefBaseline,
  BeefBreedingStageData,
  BeefGrowingStageData,
} from "../../../../models/Baseline/BeefBaseline";
import { StageType } from "../../../../models/TempBackendModels/TempCattleDB";
import { beefTabsStyles } from "../../../../../../_metronic/layout";

import TabPanel from "../../../helpers/TabPanel";
import BeefHousingBeddingSystems from "./BeefHousingBeddingSystems";

export interface BeefHousingAndManureDialogProps {
  formType: FormType;
  stageIndex: number;
  formVisible: boolean;
  handleCancel: (param?: string | null) => void;
  baseline: BeefBaseline;
  stageType: string;
}

const BeefHousingAndManureDialog: FC<BeefHousingAndManureDialogProps> = ({
  formType = FormType.Add,
  baseline,
  stageIndex = 0,
  formVisible,
  stageType,
  handleCancel,
}) => {
  const intl = useIntl();
  const classes = beefTabsStyles();
  const formContext = useFormContext<BeefIntervention>();
  const fieldItemPrefix = `stages.${stageIndex}.stageData.housing`;
  const [currentTab, setCurrentTab] = useState(0);

  const [growingEditStates, setGrowingEditStates] = useState({
    isMMSEdit: false,
  });

  const [breedingEditStates, setBreedingEditStates] = useState({
    [TargetAnimal.COWS]: {
      isMMSEdit: false,
    },
    [TargetAnimal.CALVES]: {
      isMMSEdit: false,
    },
  });

  const [isBeddingEdit, setIsBeddingEdit] = useState(false);

  const formTitle = intl.formatMessage(
    { id: "SUSTELL.PROCESS.DIALOG.STAGE.MANURE.TITLE" },
    { br: " " }
  );

  const currentStageData = baseline.stages[stageIndex].stageData;

  let housingData: CattleHousingBaseline | CattleHousingAndManureBaseline;
  switch (stageType) {
    case StageType.Growing:
      housingData = (currentStageData as BeefGrowingStageData).housing;
      break;

    case StageType.Breeding:
      housingData = (currentStageData as BeefBreedingStageData).housing;
      break;

    default:
      housingData = {} as CattleHousingAndManureBaseline;
      break;
  }

  const currResetValue = useRef<CattleHousing | CattleHousingAndManure>();

  useEffect(() => {
    currResetValue.current = cloneDeep<CattleHousingAndManure>(
      formContext.getValues(fieldItemPrefix)
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAnyEditActive = (): boolean => {
    if (isBeddingEdit) {
      return true;
    }

    if (stageType === StageType.Growing) {
      return Object.values(growingEditStates).some(Boolean);
    }

    if (stageType === StageType.Breeding) {
      return Object.values(breedingEditStates).some((animalStates) =>
        Object.values(animalStates).some(Boolean)
      );
    }

    return false;
  };

  const handleResetClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...formContext.getValues() } as BeefIntervention;

      const housingReset = resetObject.stages[stageIndex]?.stageData as
        | BeefGrowingStageDataIntervention
        | BeefBreedingStageDataIntervention;

      if (housingReset?.housing) {
        housingReset.housing = currResetValue.current;
        formContext.reset(resetObject, { errors: true });
      }
    } else {
      formContext.reset(undefined, { errors: true });
    }
    if (handleCancel) handleCancel("reset");
  };

  const handleTabChange = (event: React.ChangeEvent<any>, newValue: number) => {
    setCurrentTab(newValue);
  };

  const setEditState = (
    key: keyof typeof growingEditStates,
    value: boolean,
    target?: TargetAnimal
  ) => {
    if (stageType === StageType.Growing) {
      setGrowingEditStates((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (stageType === StageType.Breeding && target) {
      setBreedingEditStates((prev) => ({
        ...prev,
        [target]: {
          ...prev[target],
          [key]: value,
        },
      }));
    }
  };

  const getFieldItemPrefix = (target?: TargetAnimal) =>
    target ? `${fieldItemPrefix}.${target}` : fieldItemPrefix;

  const getCurrentStageData = (
    target?: Extract<keyof CattleHousingBaseline, "cows" | "calves">
  ): CattleHousingAndManureBaseline =>
    target
      ? (housingData as CattleHousingBaseline)[target]
      : (housingData as CattleHousingAndManureBaseline);

  const getCurrentFormValues = (target: TargetAnimal | undefined) =>
    target
      ? (currResetValue.current as CattleHousing)?.[target]
      : (currResetValue.current as CattleHousingAndManure);

  const renderContent = (target?: TargetAnimal) => {
    const currentFormValues = getCurrentFormValues(target);
    const baseline = getCurrentStageData(target);
    const fieldItemPrefix = getFieldItemPrefix(target);

    const editStates =
      stageType === StageType.Growing
        ? growingEditStates
        : breedingEditStates[target ?? TargetAnimal.COWS];

    return (
      <BeefHousingAndManureForm
        formType={formType}
        stageType={stageType}
        stageIndex={stageIndex}
        baseline={baseline}
        currentFormsValues={currentFormValues}
        fieldItemPrefix={fieldItemPrefix}
        isMMSEdit={editStates.isMMSEdit}
        setIsMMSEdit={(val) => setEditState("isMMSEdit", val, target)}
      />
    );
  };

  const renderGrowingContent = () => renderContent();

  const renderBreedingContent = () => (
    <>
      <Box className={classes.tabsBox}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="beef-breeding-emissions"
          className={classes.tabs}
        >
          <Tab
            label={intl.formatMessage({
              id: `SUSTELL.STAGE.BEEF.STEPS.COWS`,
            })}
          />
          <Tab
            label={intl.formatMessage({
              id: `SUSTELL.STAGE.BEEF.STEPS.CALVES`,
            })}
          />
        </Tabs>
      </Box>
      <TabPanel value={currentTab} index={0}>
        {renderContent(TargetAnimal.COWS)}
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        {renderContent(TargetAnimal.CALVES)}
      </TabPanel>
    </>
  );

  return (
    <DialogContainer
      formVisible={formVisible}
      handleClose={handleResetClick}
      iconCode="general/building-06"
      formTitle={formTitle}
      datasetType="intervention"
      width={isAnyEditActive() ? "1220px" : "1008px"}
    >
      <BeefHousingBeddingSystems
        formType={formType}
        stageIndex={stageIndex}
        baseline={baseline.stages[stageIndex] as BeefStageIntervention}
        isBeddingEdit={isBeddingEdit}
        setIsBeddingEdit={setIsBeddingEdit}
      />

      {stageType === StageType.Breeding && renderBreedingContent()}
      {stageType === StageType.Growing && renderGrowingContent()}

      <DsmButtonControlGroup
        cancelHandler={handleResetClick}
        saveHandler={async () => {
          handleCancel("confirm");
        }}
        saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
      />
    </DialogContainer>
  );
};

export default BeefHousingAndManureDialog;
